import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Badge, Button, ConfirmationModal, toast, Tooltip } from '@/components';
import { HouseholdSection } from '@/components/HouseholdSection';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { useEffect, useState } from 'react';
import { MailIcon, InfoIcon, UnlinkIcon } from '@/components/Icons';
import * as React from 'react';
import {
    childrenParentalAuthorityStatusAtom,
    profileAtom,
} from '../model/store';
import { useProfile } from '@/features/shared/lib/useProfile';
import { fetchChildrenParentalAuthorityStatusQueryAtom } from '@/features/shared/features/addOrEditChild/model';
import { addAdolescentMember } from '@/features/profile/widgets/household/api/addAdolescentMember';
import { HouseholdMemberType, PartnerStatusEnum } from '@/shared/enums';
import { getHouseholdMemberStatus } from '../lib/getHouseholdMemberStatus';
import { format } from '@/Resources.resx';
import { IAdolescentHouseholdMemberDto } from '@/shared/interfaces';
import { atomWithMutation } from 'jotai-tanstack-query';
import { deleteAdolescentHouseholdMember } from '@/features/profile/widgets/household/api/deleteAdolescentHouseholdMember';
import { AddChildrenModal } from '@/features/shared/features/addOrEditChild/ui';
import { AddChildrenMode } from '@/features/shared/features/addOrEditChild/lib';
import {
    adolescentMinimumAgeAtom,
    adultMinimumAgeAtom,
} from '@/caregiverSpace/shared/model';

const deleteMemberMutationAtom = atomWithMutation(() => ({
    mutationFn: deleteAdolescentHouseholdMember,
}));

export function AdolescentSection() {
    const setProfileAtom = useSetAtom(profileAtom);
    const profile = useAtomValue(profileAtom);
    const { refetch } = useProfile();
    const [memberToDelete, setMemberToDelete] =
        useState<IAdolescentHouseholdMemberDto | null>(null);
    const [{ mutate: deleteMember, isPending }] = useAtom(
        deleteMemberMutationAtom,
    );
    const parentalAuthorityStatuses = useAtomValue(
        childrenParentalAuthorityStatusAtom,
    );
    const setChildrenParentalAuthorityStatus = useSetAtom(
        childrenParentalAuthorityStatusAtom,
    );

    const adolescentMinimumAge = useAtomValue(adolescentMinimumAgeAtom);
    const adultMinimumAge = useAtomValue(adultMinimumAgeAtom);

    const { refetch: refetchParentalAuthorityStatus } = useAtomValue(
        fetchChildrenParentalAuthorityStatusQueryAtom,
    );

    useEffect(() => {
        if (profile.AdolescentHouseholdMembers.length > 0) {
            refetchParentalAuthorityStatus().then((result) => {
                if (result.data) {
                    setChildrenParentalAuthorityStatus(result.data);
                }
            });
        }
    }, [profile.AdolescentHouseholdMembers.length]);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Refetch and populate the profile atom again
    async function handleRefetch() {
        const updatedProfile = await refetch();

        if (updatedProfile?.error) {
            toast.addToast({
                type: 'error',
                message: R.RefreshDataError,
                autoClose: true,
            });
            return;
        }

        if (updatedProfile?.data) {
            setProfileAtom(updatedProfile.data);

            const statusResult = await refetchParentalAuthorityStatus();

            if (statusResult.data) {
                setChildrenParentalAuthorityStatus(statusResult.data);
            }
        }
    }

    const performDelete = async () => {
        if (!memberToDelete) {
            return;
        }

        deleteMember(memberToDelete, {
            onSuccess: handleRefetch,
            onSettled: () => setMemberToDelete(null),
            onError: () => {
                toast.addToast({
                    type: 'error',
                    message: R.ErrorDeletePartner,
                });
            },
        });
    };

    return (
        <HouseholdSection
            title={format(
                R.AdolescentTitle,
                adolescentMinimumAge.toString(),
                (adultMinimumAge - 1).toString(),
            )}
            action={
                <Button
                    variant="secondary"
                    outline
                    onClick={() => setIsAddModalOpen(true)}
                >
                    <MailIcon />
                    {R.AdolescentButtonTitle}
                </Button>
            }
            hasChildren={profile.AdolescentHouseholdMembers.length > 0}
        >
            {profile.AdolescentHouseholdMembers.map((adolescent, index) => {
                // Not all adolescents have a UID since they might just be invited but not created. So we match on CPR
                const parentalAuthorityStatus = parentalAuthorityStatuses.find(
                    (x) => x.Cpr === adolescent.Cpr,
                );

                return (
                    <div
                        key={adolescent.Cpr}
                        className={
                            index !==
                            profile.AdolescentHouseholdMembers.length - 1
                                ? 'border-b border-(--color-border-muted)'
                                : ''
                        }
                    >
                        <div className="flex items-center justify-between px-6 py-4">
                            <div className="flex items-center gap-2">
                                {!parentalAuthorityStatus?.IsConfirmed && (
                                    <div className="relative z-50">
                                        <Tooltip
                                            content={
                                                R.ParentalAuthorityCannotBeConfirmed
                                            }
                                        >
                                            <InfoIcon className="text-secondary-red" />
                                        </Tooltip>
                                    </div>
                                )}

                                <div className="text-lg font-medium text-(--color-text-primary)">
                                    {adolescent.Name}
                                </div>
                            </div>

                            <div className="flex items-center gap-6">
                                <Badge
                                    type={
                                        adolescent.Status ===
                                        PartnerStatusEnum.Invited
                                            ? 'attention'
                                            : 'success'
                                    }
                                >
                                    {getHouseholdMemberStatus(
                                        adolescent.Status,
                                    )}
                                </Badge>

                                <Button
                                    variant="ghost"
                                    onClick={() =>
                                        setMemberToDelete(adolescent)
                                    }
                                >
                                    <UnlinkIcon />
                                    {R.Unlink}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })}

            {isAddModalOpen && (
                <AddChildrenModal
                    title={R.AdolescentButtonTitle}
                    allowedTypes={[HouseholdMemberType.Adolescent]}
                    mode={AddChildrenMode.Profile}
                    onSubmitAdolescent={addAdolescentMember}
                    onChildAdded={() => {
                        handleRefetch();
                        setIsAddModalOpen(false);
                    }}
                    onClose={() => setIsAddModalOpen(false)}
                />
            )}

            {memberToDelete && (
                <ConfirmationModal
                    title={R.UnlinkAdolescent}
                    onClose={() => setMemberToDelete(null)}
                    message={format(
                        R.UnlinkAdolescentText,
                        adolescentMinimumAge.toString(),
                        (adultMinimumAge - 1).toString(),
                    )}
                    confirmButtonText={R.RemoveHouseholdMember}
                    confirmButtonClick={performDelete}
                    cancelButtonText={R.KeepHouseholdMember}
                    confirmIsProcessing={isPending}
                />
            )}
        </HouseholdSection>
    );
}
